import React from 'react';
import { pusherManager } from './PusherHandler';

/**
 * Initialize Pusher at Application Level
 * 
 * This component should be placed in your App.jsx or main component
 * to initialize Pusher once when your application starts.
 * 
 * Benefits of this approach:
 * - Single WebSocket connection for the entire application
 * - No reconnections when navigating between pages
 * - Better performance and resource usage
 * - Shared connection state across all components
 */

const PusherInitializer = () => {
    React.useEffect(() => {
        // Initialize Pusher when app loads
        const initializePusher = async () => {
            try {
                await pusherManager.initialize();
                console.log('Pusher initialized successfully at app level!');
            } catch (error) {
                console.error('Failed to initialize Pusher at app level:', error);
            }
        };

        initializePusher();

        // Optional: Cleanup on app unmount (usually not needed)
        return () => {
            // Only disconnect if you're sure the app is closing
            // pusherManager.disconnect();
        };
    }, []);

    // This component doesn't render anything
    return null;
};

/**
 * Usage Example in App.jsx:
 * 
 * ```jsx
 * import React from 'react';
 * import PusherInitializer from './path/to/PusherInitializer';
 * import YourOtherComponents from './components';
 * 
 * function App() {
 *   return (
 *     <div className="App">
 *       <PusherInitializer />
 *       <YourOtherComponents />
 *     </div>
 *   );
 * }
 * 
 * export default App;
 * ```
 * 
 * Alternative: Initialize in main.jsx/index.js:
 * 
 * ```jsx
 * import React from 'react';
 * import ReactDOM from 'react-dom/client';
 * import App from './App';
 * import { pusherManager } from './path/to/table-pusher';
 * 
 * // Initialize Pusher before rendering the app
 * pusherManager.initialize().catch(console.error);
 * 
 * ReactDOM.createRoot(document.getElementById('root')).render(
 *   <React.StrictMode>
 *     <App />
 *   </React.StrictMode>
 * );
 * ```
 */

export default PusherInitializer;